// Prehled spotrebovaneho materialu za obdobi — co a kolik se spotrebovalo
// (soucet pole "Kolik" po materialu a jednotce). Obdobi = datum operativy.
// Data: GET /api/material/spotreba?od=&do= (kazdy prihlaseny, jen cteni) + rozpis
// po dnech GET /api/material/dny s tlacitkem "Pridat" pro rychle zpetne dopsani
// (zapis pres report polozky — MaterialZapisDialog).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowDown, Loader2, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { isoDate, isoDateOffset, parseIsoDate } from '@/lib/dateWeek';
import { usePermissions } from '@/lib/permissions';
import { formatDateCs, formatNumberCs } from '@/lib/utils';
import type { MaterialDen, MaterialSpotreba, MaterialSpotrebaRadek } from '@/types/api';
import { MaterialZapisDialog } from '@/components/MaterialZapisDialog';
import { StavBadge } from '@/components/StavBadge';
import { richToPlain } from '@/components/ui/rich-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Razeni = 'mnozstvi' | 'material' | 'vyskyty';

function mesic(offset: number): { od: string; do: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { od: isoDate(first), do: isoDate(last) };
}

const DNY_TYDNE = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];

function zkrat(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

const collator = new Intl.Collator('cs-CZ', { sensitivity: 'base' });

function seradit(radky: MaterialSpotrebaRadek[], razeni: Razeni): MaterialSpotrebaRadek[] {
  const copy = [...radky];
  switch (razeni) {
    case 'material':
      return copy.sort(
        (a, b) =>
          collator.compare(a.material, b.material) ||
          collator.compare(a.jednotka ?? '', b.jednotka ?? ''),
      );
    case 'vyskyty':
      return copy.sort(
        (a, b) => b.pocetVyskytu - a.pocetVyskytu || collator.compare(a.material, b.material),
      );
    default:
      // Poradi od serveru: mnozstvi sestupne, nesectene radky na konci.
      return copy;
  }
}

export function MaterialPage() {
  const [od, setOd] = useState<string>(() => mesic(0).od);
  const [doDate, setDoDate] = useState<string>(() => mesic(0).do);
  const [razeni, setRazeni] = useState<Razeni>('mnozstvi');
  const [jenSMaterialem, setJenSMaterialem] = useState(false);
  // null = dialog zavreny, jinak predvyplneny den.
  const [zapisDatum, setZapisDatum] = useState<string | null>(null);
  const perms = usePermissions();

  const neplatneObdobi = !od || !doDate || od > doDate;

  const query = useQuery<MaterialSpotreba>({
    queryKey: ['material-spotreba', od, doDate],
    queryFn: () =>
      api<MaterialSpotreba>(
        `/material/spotreba?od=${encodeURIComponent(od)}&do=${encodeURIComponent(doDate)}`,
      ),
    enabled: !neplatneObdobi,
  });

  const dnyQuery = useQuery<MaterialDen[]>({
    queryKey: ['material-dny', od, doDate],
    queryFn: () =>
      api<MaterialDen[]>(
        `/material/dny?od=${encodeURIComponent(od)}&do=${encodeURIComponent(doDate)}`,
      ),
    enabled: !neplatneObdobi,
  });
  const dny = useMemo(
    () =>
      (dnyQuery.data ?? []).filter(
        (d) => !jenSMaterialem || d.polozky.some((p) => p.material.length > 0),
      ),
    [dnyQuery.data, jenSMaterialem],
  );

  const radky = useMemo(
    () => seradit(query.data?.radky ?? [], razeni),
    [query.data, razeni],
  );
  const pocetMaterialu = useMemo(
    () => new Set(radky.map((r) => r.material.toLocaleLowerCase('cs-CZ'))).size,
    [radky],
  );

  const nastavObdobi = (o: { od: string; do: string }) => {
    setOd(o.od);
    setDoDate(o.do);
  };

  const hlavicka = (label: string, key: Razeni, className?: string) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => setRazeni(key)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          razeni === key ? 'font-semibold text-foreground' : ''
        }`}
        title={`Seřadit podle: ${label.toLowerCase()}`}
      >
        {label}
        {razeni === key && <ArrowDown className="h-3 w-3" />}
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Materiál</h2>
          <p className="text-sm text-muted-foreground">
            Spotřebovaný materiál za zvolené období — sečteno podle materiálu a jednotky
          </p>
        </div>
        {perms.muzeEditovatPole('material') && (
          <Button onClick={() => setZapisDatum(isoDateOffset(-1))}>
            <Plus className="mr-2 h-4 w-4" />
            Zapsat materiál
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Období</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mat-od">Od</Label>
              <Input
                id="mat-od"
                type="date"
                value={od}
                onChange={(e) => setOd(e.target.value)}
                className="w-44"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mat-do">Do</Label>
              <Input
                id="mat-do"
                type="date"
                value={doDate}
                onChange={(e) => setDoDate(e.target.value)}
                className="w-44"
              />
            </div>
            <Button variant="outline" onClick={() => nastavObdobi(mesic(0))}>
              Tento měsíc
            </Button>
            <Button variant="outline" onClick={() => nastavObdobi(mesic(-1))}>
              Minulý měsíc
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Počítá se podle data operativy (dne, kdy se práce dělala).
          </p>
        </CardContent>
      </Card>

      {neplatneObdobi ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-muted-foreground">
              Zadejte platné období — datum „od" musí být nejpozději datum „do".
            </p>
          </CardContent>
        </Card>
      ) : query.isLoading ? (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ) : query.isError ? (
        <Card>
          <CardContent>
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(query.error as Error).message}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              Spotřeba {formatDateCs(parseIsoDate(od))} – {formatDateCs(parseIsoDate(doDate))}
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {pocetMaterialu} {pocetMaterialu === 1
                  ? 'materiál'
                  : pocetMaterialu >= 2 && pocetMaterialu <= 4
                    ? 'materiály'
                    : 'materiálů'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {radky.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                Ve zvoleném období se nezapsal žádný materiál.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {hlavicka('Materiál', 'material')}
                    <TableHead className="w-28">Jednotka</TableHead>
                    {hlavicka('Celkem', 'mnozstvi', 'w-32 text-right')}
                    {hlavicka('Výskytů', 'vyskyty', 'w-28 text-right')}
                    <TableHead className="w-28 text-right">Operativ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {radky.map((r) => (
                    <TableRow key={`${r.material}|${r.jednotka ?? '\u0000'}`}>
                      <TableCell className="font-medium">{r.material}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.jednotka === null ? '—' : r.jednotka || 'bez jednotky'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.mnozstvi === null ? (
                          <span
                            className="text-xs text-muted-foreground"
                            title={
                              r.nesectenaMnozstvi.length > 0
                                ? `Zapsáno: ${r.nesectenaMnozstvi.join('; ')}`
                                : 'Množství nebylo zapsáno'
                            }
                          >
                            {r.nesectenaMnozstvi.length > 0 ? 'nelze sečíst' : 'bez množství'}
                          </span>
                        ) : (
                          <span className="font-semibold">{formatNumberCs(r.mnozstvi)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.pocetVyskytu}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.pocetOperativ}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {radky.some((r) => r.mnozstvi === null) && (
              <p className="mt-3 text-xs text-muted-foreground">
                Řádky „nelze sečíst" / „bez množství" jsou zápisy, kde množství chybí nebo nejde
                o číslo s jednotkou (např. „cca 5 m"). Po najetí myší uvidíte, co bylo zapsáno.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {!neplatneObdobi && (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle>Po dnech</CardTitle>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={jenSMaterialem}
                onChange={(e) => setJenSMaterialem(e.target.checked)}
              />
              Jen dny se zapsaným materiálem
            </label>
          </CardHeader>
          <CardContent>
            {dnyQuery.isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : dnyQuery.isError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                Načtení selhalo: {(dnyQuery.error as Error).message}
              </div>
            ) : dny.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                {jenSMaterialem
                  ? 'Ve zvoleném období se nezapsal žádný materiál.'
                  : 'Ve zvoleném období nejsou žádné operativy.'}
              </p>
            ) : (
              <div className="divide-y">
                {dny.map((d) => {
                  const sMaterialem = d.polozky.filter((p) => p.material.length > 0);
                  const datum = parseIsoDate(d.datum);
                  return (
                    <div key={d.operativaId} className="space-y-2 py-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <Link
                          to={`/operativy/${d.operativaId}`}
                          className="font-semibold hover:underline"
                        >
                          {formatDateCs(datum)}
                        </Link>
                        <span className="text-sm text-muted-foreground">
                          {DNY_TYDNE[datum.getDay()]}
                        </span>
                        <StavBadge stav={d.stav} type="operativa" />
                        {perms.canPisatPole('material', d.stav) && d.polozky.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-auto"
                            onClick={() => setZapisDatum(d.datum)}
                          >
                            <Plus className="mr-1.5 h-4 w-4" />
                            Přidat
                          </Button>
                        )}
                      </div>
                      {sMaterialem.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Bez materiálu</p>
                      ) : (
                        <ul className="space-y-1 text-sm">
                          {sMaterialem.map((p) => (
                            <li key={p.id} className="flex flex-wrap gap-x-2">
                              <span
                                className="text-muted-foreground"
                                title={richToPlain(p.popisCinnosti)}
                              >
                                #{p.poradi} {p.mechanizaceKod} —{' '}
                                {zkrat(richToPlain(p.popisCinnosti), 50)}:
                              </span>
                              <span>
                                {p.material
                                  .map((m) => (m.kolik ? `${m.co} ${m.kolik}` : m.co))
                                  .join(', ')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <MaterialZapisDialog
        open={zapisDatum !== null}
        datum={zapisDatum ?? ''}
        onClose={() => setZapisDatum(null)}
      />
    </div>
  );
}
